import { useMemo, useState } from "react";
import { AlertTriangle, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { contrast, deriveCustom, fixText, hexToRgb, isHex, quartetOf, rgbToHex } from "@/lib/themes";
import { ThemeSwatch } from "@/components/look/ThemeTab";

const FIELDS = [
  { id: "background", label: "Background" },
  { id: "cards", label: "Cards" },
  { id: "text", label: "Text" },
  { id: "accent", label: "Accent" },
];
const BAD_HEX = "Enter a colour like #1A2B3C";

function ColorRow({ field, value, raw, error, onPicker, onHex }) {
  return (
    <div>
      <label className="q-label" htmlFor={`hex-${field.id}`}>{field.label}</label>
      <div className="flex items-center gap-2">
        <input type="color" value={value} onChange={(e) => onPicker(e.target.value.toUpperCase())} aria-label={`${field.label} colour`}
          className="h-10 w-12 flex-shrink-0 cursor-pointer rounded-lg border border-q-line bg-q-raised p-1" data-testid={`theme-editor-${field.id}-picker`} />
        <input id={`hex-${field.id}`} className="q-input font-mono uppercase" value={raw} maxLength={7} aria-invalid={!!error}
          onChange={(e) => onHex(e.target.value)} data-testid={`theme-editor-${field.id}-hex`} />
      </div>
      {error && <p className="q-error" data-testid={`theme-editor-${field.id}-error`}>{error}</p>}
    </div>
  );
}

function Preview({ colors, name }) {
  const t = deriveCustom({ id: "preview", name: name || "My theme", ...colors });
  return (
    <div className="space-y-3" data-testid="theme-editor-preview">
      <p className="q-eyebrow">Live preview</p>
      <div className="rounded-xl border p-4" style={{ background: colors.background, borderColor: `rgb(${t.line})` }}>
        <p className="font-display text-lg font-extrabold" style={{ color: colors.text }}>Good evening</p>
        <div className="mt-3 rounded-lg border p-3.5" style={{ background: colors.cards, borderColor: `rgb(${t.border})` }}>
          <p className="text-sm font-semibold" style={{ color: colors.text }}>Con Edison · due Friday</p>
          <p className="mt-1 text-xs" style={{ color: `rgb(${t.muted})` }}>Every card uses your text colour.</p>
          <span className="mt-3 inline-block rounded-md px-3 py-1.5 text-xs font-bold" style={{ background: colors.accent, color: `rgb(${t.accentInk})` }}>Accent button</span>
        </div>
      </div>
      <ThemeSwatch t={t} />
    </div>
  );
}

export default function ThemeEditor({ editing, onClose, onSaved, onLimit }) {
  const start = editing.ct ? { background: editing.ct.background, cards: editing.ct.cards, text: editing.ct.text, accent: editing.ct.accent } : quartetOf(editing.start);
  const [colors, setColors] = useState(start);
  const [raw, setRaw] = useState(start);
  const [errors, setErrors] = useState({});
  const [name, setName] = useState(editing.ct?.name || "");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const ratio = useMemo(() => {
    const t = hexToRgb(colors.text);
    return Math.min(contrast(t, hexToRgb(colors.background)), contrast(t, hexToRgb(colors.cards)));
  }, [colors]);

  const setColor = (id, v) => {
    setColors((c) => ({ ...c, [id]: v }));
    setRaw((r) => ({ ...r, [id]: v }));
    setErrors((e) => ({ ...e, [id]: null }));
  };
  const onHex = (id, v) => {
    const val = v.startsWith("#") ? v : `#${v}`;
    setRaw((r) => ({ ...r, [id]: v }));
    if (isHex(val)) setColor(id, val.toUpperCase());
    else setErrors((e) => ({ ...e, [id]: BAD_HEX }));
  };
  const fix = () => setColor("text", rgbToHex(fixText(hexToRgb(colors.text), hexToRgb(colors.background), hexToRgb(colors.cards))));

  const save = async () => {
    if (!name.trim()) { setFormError("Give your theme a name"); return; }
    setBusy(true);
    setFormError("");
    try {
      const body = { name: name.trim(), ...colors };
      const { data } = editing.ct ? await api.put(`/me/custom-themes/${editing.ct.id}`, body) : await api.post("/me/custom-themes", body);
      toast.success(`"${name.trim()}" is on`);
      onSaved(data);
    } catch (e) {
      const m = errMsg(e);
      if (m.startsWith("You can keep up to 5")) { onLimit(m); onClose(); } else setFormError(m);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl overflow-y-auto border-q-line bg-q-surface p-5 sm:p-7" data-testid="theme-editor">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-extrabold text-q-text">{editing.ct ? "Edit your theme" : "Make my own theme"}</DialogTitle>
          <DialogDescription className="text-q-muted">Pick four colours. Everything else is worked out for you.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div>
              <label className="q-label" htmlFor="theme-name">Name</label>
              <input id="theme-name" className="q-input" value={name} maxLength={40} placeholder="e.g. Midnight Mango" onChange={(e) => setName(e.target.value)} data-testid="theme-editor-name" />
            </div>
            {FIELDS.map((f) => (
              <ColorRow key={f.id} field={f} value={colors[f.id]} raw={raw[f.id]} error={errors[f.id]} onPicker={(v) => setColor(f.id, v)} onHex={(v) => onHex(f.id, v)} />
            ))}
            {ratio < 4.5 && (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border px-3.5 py-2.5 text-sm font-semibold" data-testid="theme-editor-contrast-warning"
                style={{ borderColor: "rgba(var(--glow-home),0.45)", background: "rgba(var(--glow-home),0.1)", color: "rgb(var(--q-text))" }}>
                <AlertTriangle className="h-4 w-4 text-q-accent" /> Text may be hard to read
                <button className="q-btn q-btn-secondary ml-auto px-3 py-1.5 text-xs" onClick={fix} data-testid="theme-editor-fix-btn"><Wand2 className="h-3.5 w-3.5" /> Fix it</button>
              </div>
            )}
            <p className="text-xs text-q-muted" data-testid="theme-editor-contrast-ratio">Text contrast {ratio.toFixed(1)}:1 (4.5:1 or more reads well)</p>
          </div>
          <Preview colors={colors} name={name} />
        </div>
        {formError && <p className="q-error" data-testid="theme-editor-error">{formError}</p>}
        <div className="mt-2 flex flex-wrap justify-end gap-2">
          <button className="q-btn q-btn-ghost" onClick={onClose} data-testid="theme-editor-cancel-btn">Cancel</button>
          <button className="q-btn q-btn-primary" onClick={save} disabled={busy} data-testid="theme-editor-save-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save theme
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
